import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import StayScrolled from 'react-stay-scrolled';

import { intlShape, injectIntl } from 'react-intl';
import { Message, Session } from 'models';
import { debounce, isMobile } from 'globals/utils';

import styles, { afterHoursStyles } from './styles';
import ChatMessage from '../messages/ChatMessage';
import ChatTypingIndicator from '../messages/ChatTypingIndicator';
import ChatConversationEndButton from '../ChatConversationEndButton';
import ChatConversationEventBanner from '../ChatConversationEventBanner';
import ChatConversationSatisfactionSurvey from '../ChatConversationSatisfactionSurvey';

import {
  getActiveSession,
  getActiveSessionMessages,
  getCompanyClosableChatEnabled,
  getScrollToMessageId,
  isSessionLocked,
  selectIsAfterHours,
  selectOfflineImageUrl,
  getActiveSessionSocketTypingActivity,
} from '../../app/ChatApp/selectors';

import {
  handleChatFetchNextMessagesForActiveSession,
  chatResetScrollToMessageId,
  maybeGetSatisfactionForm,
} from '../../app/ChatApp/actions';

import componentMessages from './messages';

import { handleChatRetryMessageSend } from '../ChatConversationInput/actions';

export class ChatConversationThread extends PureComponent {
  constructor(props) {
    super(props);
    this.storeScrolledControllers = this.storeScrolledControllers.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleScrollToBottom = this.handleScrollToBottom.bind(this);
    this.handleScrollToDOMNode = this.handleScrollToDOMNode.bind(this);
    this.storeDOM = this.storeDOM.bind(this);
    this.stayScrolled = () => {};
    this.scrollDOM = null;
    this.messageDOMRefs = [];
    this.debouncedScrollHandler = debounce(
      () => {
        props.onScroll();
      },
      1000,
      true,
    );
  }

  componentDidMount() {
    this.handleMaybeFetchSatisfactionForm();
  }

  componentDidUpdate(prevProps) {
    const { session, messages, scrollToMessageId, resetScrollToMessageId, isVolumeControl, isLocked } = this.props;

    if (!prevProps.session.get('id') && session.get('id')) {
      this.handleMaybeFetchSatisfactionForm();
    }

    if (isVolumeControl) {
      this.stayScrolled();
      return;
    }

    if (scrollToMessageId) {
      const messageDOM = this.messageDOMRefs[scrollToMessageId];

      if (messageDOM) {
        messageDOM.scrollIntoView();
      }

      resetScrollToMessageId();
    } else if (prevProps.messages.size < messages.size || prevProps.isLocked !== isLocked) {
      this.scrollBottom();
    }
  }

  handleMaybeFetchSatisfactionForm = () => {
    const { session } = this.props;

    const hasSatisfactionForm = session && !!session.get('satisfactionId', {});

    if (hasSatisfactionForm) {
      this.props.fetchSatisfactionForm(session.id);
    }
  };

  storeScrolledControllers({ stayScrolled, scrollBottom }) {
    this.stayScrolled = stayScrolled;
    this.scrollBottom = scrollBottom;
  }

  handleScroll() {
    if (this.scrollDOM.scrollTop < 100) {
      this.debouncedScrollHandler();
    }
  }

  handleScrollToBottom() {
    if (this.scrollBottom) this.scrollBottom();
  }

  handleScrollToDOMNode(dom) {
    if (dom) dom.scrollIntoView();
  }

  storeDOM(dom) {
    this.scrollDOM = dom;
  }

  renderThread() {
    const { messages, retryMessageSend } = this.props;

    const messagesList = messages.toList();
    return messagesList.map((message, index) => {
      const previous = index > 0 ? messagesList.get(index - 1) : null;
      const short = previous && previous.sentBy.id === message.sentBy.id;
      const isLastMessageInSession = index === messagesList.size - 1;

      return (
        <ChatMessage
          key={message.id}
          isLastMessageInSession={isLastMessageInSession}
          message={message}
          short={short}
          provideDOMNode={dom => {
            this.messageDOMRefs[message.id] = dom;
          }}
          onRetry={retryMessageSend}
        />
      );
    });
  }

  renderSatisfactionSurvey = () => {
    const { isLocked, session } = this.props;

    const satisfactionId = session && session.get('satisfactionId', {});

    if (!isLocked || !satisfactionId) return null;

    return (
      <div style={styles.messagesContainer}>
        <ChatConversationSatisfactionSurvey
          onScrollToBottom={this.handleScrollToBottom}
          onScrollToDOMNode={this.handleScrollToDOMNode}
        />
      </div>
    );
  };

  renderTypingIndicator = () => {
    const { typingStatus, session } = this.props;

    if (!typingStatus || typingStatus === 'typing-ended') return null;

    return <ChatTypingIndicator session={session} />;
  };

  renderMessagesContainer = () => {
    return (
      <div>
        <ChatConversationEndButton />
        <div style={styles.messagesContainer}>
          {this.renderThread()}
          {this.renderTypingIndicator()}
        </div>
      </div>
    );
  };

  renderMobileConversationThread = () => {
    const { isAfterHours, offlineImageUrl, messages, intl, isLocked } = this.props;

    return (
      <StayScrolled
        component="div"
        style={isAfterHours && !messages.size ? afterHoursStyles(offlineImageUrl) : styles.mobileScrollContainer}
        provideControllers={this.storeScrolledControllers}
        onScroll={this.handleScroll}
        stayInaccuracy={100}
        _provideDOMNode={this.storeDOM}
        className="kustomer-app-thread"
        tabIndex="0"
        aria-label={isAfterHours && !messages.size ? intl.formatMessage(componentMessages.aria_label_after_hours) : ''}
      >
        <div style={styles.mobileMessagesContainer}>
          {this.renderMessagesContainer()}
          <ChatConversationEventBanner isLocked={isLocked} />
          {this.renderSatisfactionSurvey()}
        </div>
      </StayScrolled>
    );
  };

  render() {
    const { isAfterHours, offlineImageUrl, messages, intl, isLocked } = this.props;

    if (isMobile()) {
      return this.renderMobileConversationThread();
    }

    return (
      <StayScrolled
        component="div"
        style={isAfterHours && !messages.size ? afterHoursStyles(offlineImageUrl) : styles.alley}
        provideControllers={this.storeScrolledControllers}
        onScroll={this.handleScroll}
        stayInaccuracy={100}
        _provideDOMNode={this.storeDOM}
        className="kustomer-app-thread"
        tabIndex="0"
        aria-label={isAfterHours && !messages.size ? intl.formatMessage(componentMessages.aria_label_after_hours) : ''}
      >
        {this.renderMessagesContainer()}
        <ChatConversationEventBanner isLocked={isLocked} />
        {this.renderSatisfactionSurvey()}
      </StayScrolled>
    );
  }
}

ChatConversationThread.propTypes = {
  messages: ImmutablePropTypes.orderedSetOf(React.PropTypes.instanceOf(Message)),
  session: React.PropTypes.instanceOf(Session),
  scrollToMessageId: React.PropTypes.string,
  onScroll: React.PropTypes.func,
  resetScrollToMessageId: React.PropTypes.func,
  retryMessageSend: React.PropTypes.func,
  isAfterHours: React.PropTypes.bool,
  isVolumeControl: React.PropTypes.bool,
  offlineImageUrl: React.PropTypes.string,
  isLocked: React.PropTypes.bool,
  fetchSatisfactionForm: React.PropTypes.func.isRequired,
  typingStatus: React.PropTypes.string,
  intl: intlShape.isRequired,
};

ChatConversationThread.defaultProps = {
  messages: Immutable.OrderedSet(),
  session: null,
  scrollToMessageId: null,
  isVolumeControl: false,
  onScroll: () => {},
  resetScrollToMessageId: () => {},
  retryMessageSend: () => {},
  isLocked: false,
  typingStatus: undefined,
};

export const mapStateToProps = state => {
  const session = getActiveSession(state);
  const closableChatEnabled = getCompanyClosableChatEnabled(state);

  return {
    session,
    isLocked: closableChatEnabled && isSessionLocked(state, session.id),
    isAfterHours: selectIsAfterHours(state),
    offlineImageUrl: selectOfflineImageUrl(state),
    messages: getActiveSessionMessages(state),
    scrollToMessageId: getScrollToMessageId(state),
    typingStatus: getActiveSessionSocketTypingActivity(state),
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    fetchSatisfactionForm(sessionId) {
      dispatch(maybeGetSatisfactionForm(sessionId));
    },
    onScroll() {
      dispatch(handleChatFetchNextMessagesForActiveSession());
    },
    resetScrollToMessageId() {
      dispatch(chatResetScrollToMessageId());
    },
    retryMessageSend(messageId) {
      dispatch(handleChatRetryMessageSend(messageId));
    },
  };
};

export const componentWithIntl = injectIntl(ChatConversationThread, { withRef: true });
export default connect(mapStateToProps, mapDispatchToProps)(componentWithIntl);
